import { useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { api, ApiError } from "../lib/api.ts";
import { OrgProvider, type Me } from "../lib/org.tsx";
import { FullPageSpinner } from "./Spinner.tsx";

export function AuthGate({ children, requireOrg }: { children: ReactNode; requireOrg: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ["me"], queryFn: () => api.get<Me>("/me"), retry: false, staleTime: 60_000 });
  const unverified = me.error instanceof ApiError && me.error.code === "email_not_verified";
  const unauthorized = me.error instanceof ApiError && (me.error.status === 401 || unverified);
  const needsOnboarding = requireOrg && me.data?.organizations.length === 0;
  // Redirect from an effect that runs once per condition: rendering <Navigate> here re-fired on
  // every render while the route transition was pending and locked up the page.
  useEffect(() => {
    if (unverified) void navigate({ to: "/verify-email", replace: true });
    else if (unauthorized) void navigate({ to: "/login", search: { redirect: location.href }, replace: true });
    else if (needsOnboarding) void navigate({ to: "/onboarding", replace: true });
    // location.href is read only when redirecting to login.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unverified, unauthorized, needsOnboarding, navigate]);
  if (me.isPending || unauthorized || needsOnboarding) return <FullPageSpinner />;
  if (me.error) return <div className="p-10 text-center text-sm text-destructive">Could not reach the server. Please retry.</div>;
  return <OrgProvider me={me.data}>{children}</OrgProvider>;
}
